import { saveAs } from 'file-saver';

const DEFAULT_APP_TOKEN = () => localStorage.getItem('appToken');

export interface ApiResponse<T = any> {
  status: number;
  data: T;
}

// helper to resolve token
const resolveToken = (token: string | boolean | undefined): string | null => {
  if (token === false) return null;
  if (token === true || token === undefined) return DEFAULT_APP_TOKEN();
  return token || DEFAULT_APP_TOKEN();
};

// Query param serializer (optional, reusable)
export const serializeParams = (params: Record<string, any> = {}): string => {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : '';
};

// POST utility
export const postData = async (
  url: string,
  token?: string | boolean,
  body: Record<string, any> = {}
): Promise<ApiResponse> => {
  const resolvedToken =
    token === false
      ? null
      : token === true || token === undefined
      ? localStorage.getItem('appToken') || localStorage.getItem('apptoken') || localStorage.getItem('token')
      : token;
  console.log('POST Request:', url, body, resolvedToken);

  try {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(resolvedToken ? { apptoken: resolvedToken } : {}),
      },
      body: JSON.stringify(body),
    });

    const contentType = response.headers.get('content-type');
    let responseBody: any;

    if (contentType && contentType.includes('application/json')) {
      responseBody = await response.json();
    } else {
      const text = await response.text();
      responseBody = { message: 'Invalid JSON response', raw: text };
    }

    const status = responseBody.statusCode || response.status;

    return { status, data: responseBody };
  } catch (error: any) {
    return { status: 500, data: { message: error.message } };
  }
};


// GET utility
export const getData = async (
  url: string | { url: string; params?: Record<string, any>; token?: string | boolean },
  tokenOrParams?: string | boolean | Record<string, any>,
  params?: Record<string, any>
): Promise<ApiResponse> => {
  let targetUrl = '';
  let actualParams: Record<string, any> = {};
  let actualToken: string | boolean | undefined = undefined;

  if (typeof url === 'object' && url !== null) {
    targetUrl = url.url;
    actualParams = url.params || {};
    actualToken = url.token;
  } else {
    targetUrl = url;
    if (
      typeof tokenOrParams === 'object' &&
      tokenOrParams !== null &&
      !Array.isArray(tokenOrParams)
    ) {
      actualParams = tokenOrParams;
    } else {
      actualToken = tokenOrParams as string | boolean | undefined;
      actualParams = params || {};
    }
  }

  if (targetUrl && !targetUrl.startsWith('http://') && !targetUrl.startsWith('https://')) {
    const rawServerUrl = localStorage.getItem('serverUrl') || 'https://sfa.uat.nitiapp.com/';
    const serverUrl = rawServerUrl.endsWith('/') ? rawServerUrl : `${rawServerUrl}/`;
    targetUrl = `${serverUrl}${targetUrl.replace(/^\//, '')}`;
  }

  const resolvedToken =
    actualToken === false
      ? null
      : actualToken === true || actualToken === undefined
      ? localStorage.getItem('appToken') || localStorage.getItem('apptoken') || localStorage.getItem('token')
      : actualToken;

  const cleanedParams: Record<string, any> = {};
  if (actualParams && typeof actualParams === 'object') {
    Object.keys(actualParams).forEach((key) => {
      const val = actualParams[key];
      if (val !== undefined && val !== null && val !== 'undefined' && val !== 'null' && val !== '') {
        cleanedParams[key] = val;
      }
    });
  }

  const queryString = new URLSearchParams(cleanedParams).toString();
  const fullUrl = queryString ? `${targetUrl}?${queryString}` : targetUrl;

  console.log(fullUrl, '========Get========', resolvedToken);

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(resolvedToken ? { apptoken: resolvedToken } : {}),
      },
    });

    const contentType = response.headers.get('content-type');
    let responseBody: any;

    if (contentType && contentType.includes('application/json')) {
      responseBody = await response.json();
    } else {
      const text = await response.text();
      responseBody = { message: 'Invalid JSON response', raw: text };
    }

    const status = responseBody.statusCode || response.status;

    return { status, data: responseBody };
  } catch (error: any) {
    return { status: 500, data: { message: error.message } };
  }
};

// Download utility
export const downloadFile = async (
  url: string,
  tokenOrParams?: string | boolean | Record<string, any>,
  params?: Record<string, any>,
  fallbackFilename?: string
): Promise<{ ok: boolean; filename?: string; error?: string }> => {
  let actualParams: Record<string, any> = {};
  let actualToken: string | boolean | undefined = undefined;

  if (
    typeof tokenOrParams === 'object' &&
    tokenOrParams !== null &&
    !Array.isArray(tokenOrParams)
  ) {
    actualParams = tokenOrParams;
  } else {
    actualToken = tokenOrParams as string | boolean | undefined;
    actualParams = params || {};
  }

  const resolvedToken =
    actualToken === false
      ? null
      : actualToken === true || actualToken === undefined
      ? localStorage.getItem('appToken')
      : actualToken;

  const queryString = new URLSearchParams(actualParams).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...(resolvedToken ? { apptoken: resolvedToken as string } : {}),
      },
    });

    if (!response.ok) {
      const text = await response.text();
      return { ok: false, error: text || `HTTP ${response.status}` };
    }

    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const headerFilename = decodeURIComponent(match?.[1] || match?.[2] || '');
    const filename = headerFilename || fallbackFilename || 'download';

    const blob = await response.blob();
    saveAs(blob, filename);

    return { ok: true, filename };
  } catch (err: any) {
    return { ok: false, error: err?.message || 'Download failed' };
  }
};

